import React from 'react';

/** Icons the CTA button can show (24×24, drawn with the text color). */
const ICON_PATHS: Record< string, { label: string; svg: React.ReactNode } > = {
	'arrow-right': {
		label: 'Arrow',
		svg: <path d="M5 12h14M13 6l6 6-6 6" />,
	},
	'arrow-up-right': {
		label: 'Arrow up-right',
		svg: <path d="M7 17 17 7M8 7h9v9" />,
	},
	'chevron-right': {
		label: 'Chevron',
		svg: <path d="m9 6 6 6-6 6" />,
	},
	cart: {
		label: 'Cart',
		svg: (
			<>
				<path d="M3 4h2l2.4 11h10.8L21 7H6.2" />
				<circle cx="9" cy="19.5" r="1.5" />
				<circle cx="17" cy="19.5" r="1.5" />
			</>
		),
	},
	tag: {
		label: 'Tag',
		svg: (
			<>
				<path d="M3 12V4h8l10 10-8 8z" />
				<circle cx="7.5" cy="8" r="1.5" />
			</>
		),
	},
	gift: {
		label: 'Gift',
		svg: (
			<>
				<path d="M3 8h18v4H3zM5 12v9h14v-9M12 8v13" />
				<path d="M12 8C10.5 4.5 6 4 6 6.5 6 8 9 8 12 8c3 0 6 0 6-1.5C18 4 13.5 4.5 12 8z" />
			</>
		),
	},
	bolt: {
		label: 'Lightning',
		svg: <path d="M13 2 4 14h7l-1 8 9-12h-7z" />,
	},
	play: {
		label: 'Play',
		svg: <path d="M8 5v14l11-7z" />,
	},
	download: {
		label: 'Download',
		svg: <path d="M12 4v11M7 10l5 5 5-5M5 20h14" />,
	},
	star: {
		label: 'Star',
		svg: (
			<path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2L12 17.3l-5.6 2.9 1.1-6.2L3 9.6l6.2-.9z" />
		),
	},
};

/** Options for the icon select ("none" first). */
export const CTA_ICON_OPTIONS = [
	{ value: 'none', label: 'No icon' },
	...Object.keys( ICON_PATHS ).map( ( value ) => ( {
		value,
		label: ICON_PATHS[ value ].label,
	} ) ),
];

interface CtaIconProps {
	name: string;
	/** Size in px or any CSS length (default 1em: the text size). */
	size?: string;
	className?: string;
}

/**
 * One CTA icon, or nothing for "none" / unknown names.
 *
 * @param props Icon name and size.
 */
export const CtaIcon: React.FC< CtaIconProps > = ( {
	name,
	size = '1em',
	className,
} ) => {
	const icon = ICON_PATHS[ name ];
	if ( ! icon ) {
		return null;
	}
	return (
		<svg
			className={ className }
			width={ size }
			height={ size }
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={ 2 }
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden
			focusable="false"
		>
			{ icon.svg }
		</svg>
	);
};
